import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CONTACT_EMAIL } from './constants';
import {
  ORGANIZATION_ID,
  SITE_URL,
  WEBSITE_ID,
} from './seoContent';
import './openTools.css';

const ENDPOINT = 'https://api.devssoft.com/mcp/open-tools';
const PAGE_URL = `${SITE_URL}/herramientas-abiertas/`;
const title = 'MCP de herramientas abiertas de Devssoft | Documentación';
const description =
  'Documentación pública del MCP de Devssoft: conexión JSON-RPC 2.0 mediante POST, tools/list y ejemplos verificados para simulaciones de préstamos.';

const initializeExample = `curl -X POST ${ENDPOINT} \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "initialize",
    "params": {
      "protocolVersion": "2025-06-18",
      "capabilities": {},
      "clientInfo": {
        "name": "mi-cliente",
        "version": "1.0.0"
      }
    }
  }'`;

const listExample = `curl -X POST ${ENDPOINT} \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 2,
    "method": "tools/list",
    "params": {}
  }'`;

const calculateExample = `curl -X POST ${ENDPOINT} \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 3,
    "method": "tools/call",
    "params": {
      "name": "calculate_loan",
      "arguments": {
        "principal": 10000,
        "annualRate": 12,
        "months": 12,
        "extraPayments": [],
        "currency": "USD"
      }
    }
  }'`;

const exportExample = `{
  "jsonrpc": "2.0",
  "id": 4,
  "method": "tools/call",
  "params": {
    "name": "export_loan_excel",
    "arguments": {
      "principal": 10000,
      "annualRate": 12,
      "months": 12,
      "currency": "USD"
    }
  }
}`;

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${PAGE_URL}#webpage`,
      url: PAGE_URL,
      name: title,
      description,
      inLanguage: 'es',
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORGANIZATION_ID },
      breadcrumb: { '@id': `${PAGE_URL}#breadcrumb` },
    },
    {
      '@type': 'TechArticle',
      '@id': `${PAGE_URL}#article`,
      headline: 'MCP de herramientas abiertas de Devssoft',
      description,
      inLanguage: 'es',
      author: { '@id': ORGANIZATION_ID },
      publisher: { '@id': ORGANIZATION_ID },
      mainEntityOfPage: { '@id': `${PAGE_URL}#webpage` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${PAGE_URL}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Devssoft',
          item: `${SITE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Herramientas abiertas',
          item: PAGE_URL,
        },
      ],
    },
  ],
};

function CodeBlock({ children, label }) {
  return (
    <div className="mcp-code">
      <span>{label}</span>
      <pre tabIndex="0"><code>{children}</code></pre>
    </div>
  );
}

export default function OpenToolsPage() {
  return (
    <div className="mcp-page">
      <Helmet>
        <html lang="es" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
        <meta name="theme-color" content="#0E2540" />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Devssoft" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={`${SITE_URL}/brand/devssoft-og.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <a className="mcp-skip" href="#documentacion">Saltar a la documentación</a>
      <header className="mcp-header">
        <Link to="/" className="mcp-brand" aria-label="Devssoft, página principal">
          <img src="/brand/landing/logo-white.png" alt="Devssoft" />
        </Link>
        <nav aria-label="Navegación de documentación">
          <a href="#conexion">Conexión</a>
          <a href="#herramientas">Herramientas</a>
          <a href="#ejemplos">Ejemplos</a>
          <Link to="/">Volver a Devssoft</Link>
        </nav>
      </header>

      <main id="documentacion">
        <section className="mcp-hero">
          <div className="mcp-wrap">
            <p className="mcp-eyebrow">DEVSSOFT · MCP PÚBLICO</p>
            <h1>Herramientas abiertas para asistentes compatibles con MCP</h1>
            <p>
              Devssoft publica un endpoint MCP sin autenticación para descubrir y ejecutar
              herramientas de uso abierto. Actualmente ofrece simulaciones educativas de
              préstamos y exportación de sus resultados a Excel.
            </p>
            <div className="mcp-endpoint">
              <span>Endpoint</span>
              <code>{ENDPOINT}</code>
              <strong>POST</strong>
            </div>
            <aside>
              Visitar esta página no instala ni conecta herramientas automáticamente. Debes
              configurar el endpoint en un cliente o asistente compatible con MCP.
            </aside>
          </div>
        </section>

        <section className="mcp-section mcp-wrap" id="conexion">
          <p className="mcp-eyebrow">01 · CONEXIÓN</p>
          <h2>JSON-RPC 2.0 sobre POST</h2>
          <p>
            El servidor funciona en modo stateless. Los mensajes MCP se envían mediante
            solicitudes <code>POST</code> con cuerpo JSON-RPC 2.0 y los encabezados indicados
            abajo.
          </p>
          <div className="mcp-note">
            <strong>GET no lista ni ejecuta herramientas.</strong>
            <p>
              Una solicitud GET devuelve HTTP 405 con el error
              <code>{'{"jsonrpc":"2.0","id":null,"error":{"code":-32000,"message":"Use POST for stateless MCP"}}'}</code>.
            </p>
          </div>
          <h3>1. Inicializar</h3>
          <p>
            El servidor confirmó el protocolo <code>2025-06-18</code>, el nombre
            <code>Devssoft Open Tools</code> y capacidades de herramientas y recursos.
          </p>
          <CodeBlock label="Solicitud initialize">{initializeExample}</CodeBlock>
          <h3>2. Descubrir herramientas</h3>
          <CodeBlock label="Solicitud tools/list">{listExample}</CodeBlock>
        </section>

        <section className="mcp-section mcp-section-alt" id="herramientas">
          <div className="mcp-wrap">
            <p className="mcp-eyebrow">02 · HERRAMIENTAS VERIFICADAS</p>
            <h2>Herramientas disponibles al 2 de octubre de 2026</h2>
            <div className="mcp-tools">
              <article>
                <span>READ ONLY · IDEMPOTENTE</span>
                <h3><code>calculate_loan</code></h3>
                <p>
                  Calcula una simulación con cuota base, intereses, saldo y plan de pagos por
                  mes. Permite comparar pagos extra al capital.
                </p>
                <h4>Resultado</h4>
                <ul>
                  <li>Resumen: cuota, interés total, total pagado y meses pagados.</li>
                  <li>Plan mensual con saldo, interés, principal y amortización extra.</li>
                  <li>Contenido estructurado y explicación breve.</li>
                </ul>
              </article>
              <article>
                <span>READ ONLY · IDEMPOTENTE</span>
                <h3><code>export_loan_excel</code></h3>
                <p>
                  Genera un informe Excel reproducible con las condiciones, el resumen y el
                  plan de pagos de la simulación.
                </p>
                <h4>Resultado</h4>
                <ul>
                  <li>Nombre y tipo MIME del archivo XLSX.</li>
                  <li>Recurso MCP con el archivo codificado para descarga.</li>
                  <li>Resumen estructurado de la simulación.</li>
                </ul>
              </article>
            </div>

            <h3>Esquema de entrada compartido</h3>
            <div className="mcp-table-wrap">
              <table>
                <caption>Parámetros aceptados por ambas herramientas</caption>
                <thead>
                  <tr><th>Campo</th><th>Tipo</th><th>Reglas verificadas</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>principal</code></td><td>number</td><td>Obligatorio, mayor que 0, máximo 1.000.000.000.000.</td></tr>
                  <tr><td><code>annualRate</code></td><td>number</td><td>Obligatorio, porcentaje nominal anual entre 0 y 1000.</td></tr>
                  <tr><td><code>months</code></td><td>integer</td><td>Obligatorio, entre 1 y 600 meses.</td></tr>
                  <tr><td><code>extraPayments</code></td><td>number[]</td><td>Opcional; hasta 600 importes no negativos, desde el mes 1.</td></tr>
                  <tr><td><code>currency</code></td><td>string</td><td>Opcional; código de tres letras mayúsculas. Valor predeterminado: USD.</td></tr>
                </tbody>
              </table>
            </div>
            <div className="mcp-warning">
              Estas herramientas generan simulaciones educativas. No aprueban créditos, no
              realizan scoring, no constituyen una oferta y no proporcionan asesoramiento
              financiero.
            </div>
          </div>
        </section>

        <section className="mcp-section mcp-wrap" id="ejemplos">
          <p className="mcp-eyebrow">03 · EJEMPLOS</p>
          <h2>Ejecutar una herramienta</h2>
          <p>
            Usa <code>tools/call</code> con el nombre exacto de la herramienta y argumentos que
            cumplan su esquema. El siguiente ejemplo fue ejecutado correctamente contra el
            endpoint público.
          </p>
          <CodeBlock label="tools/call · calculate_loan">{calculateExample}</CodeBlock>
          <p>
            Para exportar la misma simulación a Excel, conserva los argumentos y cambia el
            nombre de la herramienta:
          </p>
          <CodeBlock label="tools/call · export_loan_excel">{exportExample}</CodeBlock>
        </section>

        <section className="mcp-cta">
          <div className="mcp-wrap">
            <h2>¿Necesitas integrar herramientas o datos de tu negocio?</h2>
            <p>
              Cuéntanos el proceso que quieres conectar. Evaluaremos si MCP, una API o una
              integración personalizada es la opción adecuada.
            </p>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </div>
        </section>
      </main>

      <footer className="mcp-footer mcp-wrap">
        <span>© 2026 Devssoft · Bolivia</span>
        <div><Link to="/">Inicio</Link><Link to="/apps">Herramientas</Link></div>
      </footer>
    </div>
  );
}
